import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PaperPlaneRight, Paperclip, X, Spinner, ArrowBendUpLeft } from '@phosphor-icons/react';

import EmojiPicker from './EmojiPicker';
import useAttachments from './useAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Chat message composer — textarea + @mentions + attachments + emoji picker.
 * Sub-concerns are extracted:
 *   - <EmojiPicker>         — the emoji panel + click-away behaviour.
 *   - useAttachments()      — upload / remove / reset state for files.
 * This keeps Composer focused on text editing + send.
 */
export default function Composer({ directory, channelId, onSend, onTyping, replyTo, onCancelReply, placeholder }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const lastTypingRef = useRef(0);
  const { attachments, uploading, onFiles, remove: removeAttachment, reset: resetAttachments } = useAttachments();

  const matches = mentionQuery !== null
    ? directory.filter((u) => (u.name || u.email || '').toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  const autoGrow = (el) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);
    autoGrow(e.target);
    const caret = e.target.selectionStart;
    const m = val.slice(0, caret).match(/@([^\s@]*)$/);
    setMentionQuery(m ? m[1] : null);
    // Throttled typing ping (max once / 2s)
    const now = Date.now();
    if (onTyping && val && now - lastTypingRef.current > 2000) {
      lastTypingRef.current = now;
      onTyping();
    }
  };

  const insertMention = (u) => {
    const name = u.name || u.email;
    const caret = taRef.current?.selectionStart ?? text.length;
    const upto = text.slice(0, caret).replace(/@([^\s@]*)$/, `@${name} `);
    setText(upto + text.slice(caret));
    setMentionQuery(null);
    taRef.current?.focus();
  };

  const insertEmoji = (emoji) => {
    const el = taRef.current;
    const caret = el?.selectionStart ?? text.length;
    const next = text.slice(0, caret) + emoji + text.slice(caret);
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = caret + emoji.length;
      el.setSelectionRange(pos, pos);
      autoGrow(el);
    });
  };

  const send = async () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    const mentions = directory
      .filter((u) => body.includes(`@${u.name || u.email}`))
      .map((u) => u.id);
    setSending(true);
    try {
      await onSend({ text: body, attachments, mentions });
      setText('');
      resetAttachments();
      setMentionQuery(null);
      if (taRef.current) taRef.current.style.height = 'auto';
    } catch (err) {
      console.error('[chat] send failed', err);
      toast.error('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && mentionQuery === null) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="border-t border-zinc-200 bg-white p-3 relative" data-testid="chat-composer">
      {replyTo && (
        <div className="flex items-center gap-2 mb-2 px-2.5 py-1.5 bg-zinc-50 border-l-2 border-teal-700 rounded text-xs text-zinc-600" data-testid="reply-context">
          <ArrowBendUpLeft size={14} className="text-teal-700 shrink-0" />
          <span className="truncate"><b className="text-zinc-700"><span data-no-translate>{replyTo.user_name}</span></b> に返信：{replyTo.text || '📎 添付ファイル'}</span>
          <button type="button" onClick={onCancelReply} data-testid="reply-cancel" className="ml-auto text-zinc-400 hover:text-red-600"><X size={13} /></button>
        </div>
      )}
      {mentionQuery !== null && matches.length > 0 && (
        <div className="absolute bottom-full left-3 mb-1 w-64 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden z-20" data-testid="mention-popup">
          {matches.map((u) => (
            <button key={u.id} type="button" onClick={() => insertMention(u)}
              data-testid={`mention-option-${u.id}`}
              className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-2 text-sm">
              <span className="w-6 h-6 rounded-full bg-teal-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                {(u.name || u.email || '?').charAt(0).toUpperCase()}
              </span>
              <span className="truncate">{u.name || u.email}</span>
            </button>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a, i) => (
            <span key={a.file_id}
              data-testid={`chat-attachment-${a.file_id}`}
              className="inline-flex items-center gap-1.5 bg-zinc-100 rounded-md pl-2 pr-1 py-1 text-xs text-zinc-700">
              <Paperclip size={12} /> <span className="max-w-[140px] truncate">{a.name}</span>
              <button type="button" onClick={() => removeAttachment(i)}
                data-testid={`chat-attachment-remove-${a.file_id}`}
                className="hover:text-red-600">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          data-testid="chat-attach-btn"
          className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 active:scale-90 transition-all">
          {uploading ? <Spinner size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </button>
        <input ref={fileRef} type="file" multiple hidden onChange={onFiles} data-testid="chat-file-input" />
        <EmojiPicker onPick={insertEmoji} />
        <textarea
          ref={taRef}
          value={text}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={placeholder || 'メッセージを入力...（@でメンション、Enterで送信）'}
          data-testid="chat-message-input"
          className="flex-1 resize-none max-h-32 px-3 py-2 rounded-lg border border-zinc-300 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-teal-700/30 focus:border-teal-700"
        />
        <button type="button" onClick={send} disabled={sending || (!text.trim() && attachments.length === 0)}
          data-testid="chat-send-btn"
          className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg bg-teal-700 text-white hover:bg-blue-700 active:scale-90 disabled:opacity-40 disabled:active:scale-100 transition-all">
          <PaperPlaneRight size={18} weight="fill" />
        </button>
      </div>
    </div>
  );
}
